<div #searchWrapper class="search-wrapper">

  <!-- ── Trigger button ──────────────────────────────────────────────────── -->
  <button class="search-trigger" type="button" (click)="open()" aria-label="Search" aria-haspopup="dialog"
    [attr.aria-expanded]="isOpen()">
    <lucide-icon name="search" [size]="18" style="display: flex;" />
  </button>

  <!-- ── Modal ────────────────────────────────────────────────────────────── -->
  @if (isOpen()) {
  <div class="search-overlay" (click)="close()">
    <div class="search-modal" role="dialog" aria-modal="true" aria-label="Search" (click)="$event.stopPropagation()">

      <!-- Search row -->
      <div class="modal-search-row">
        <div class="search-row-main">
        <div class="dk-field-wrap">
          <span class="dk-field-wrap__addon dk-field-wrap__addon--start" aria-hidden="true">
            <lucide-icon name="search" [size]="18" />
          </span>
          <input #searchInput class="dk-field search-input" type="text" placeholder="Search..." autocomplete="off"
            spellcheck="false" [value]="searchQuery()" (input)="onInput($event)" (keydown)="onKeyDown($event)"
            aria-label="Search" role="combobox" aria-haspopup="listbox" [attr.aria-expanded]="true" />
        </div>
        <!-- What the filter holds depends on the tab; filtering does not apply to Recent -->
        @if (activeTab() === 'recent') {
        <button class="dk-btn dk-btn--ghost dk-btn--icon dk-btn--sm" type="button" aria-label="Filter" disabled>
          <lucide-icon name="list-filter" [size]="18" />
        </button>
        } @else {
        <app-toolbar-popover #filterMenu label="Filter" icon="list-filter" align="end" [active]="filterActive()">
          @switch (activeTab()) {
          @case ('all-projects') {
          <app-search-filter-panel [sort]="projectsSort()" (sortChange)="projectsSort.set($event)" />
          }
          @case ('requests-assigned') {
          <app-search-filter-panel [sort]="assignedSort()" (sortChange)="setAssignedSort($event)"
            [statusOptions]="requestStatusOptions" [statuses]="assignedStatuses()"
            (statusesChange)="setAssignedStatuses($event)" />
          }
          @case ('pending-approvals') {
          <app-search-filter-panel [sort]="pendingSort()" (sortChange)="setPendingSort($event)" />
          }
          }
        </app-toolbar-popover>
        }
        </div>

        @if (statusChips().length > 0) {
        <ul class="filter-chips" aria-label="Active filters">
          @for (chip of statusChips(); track chip.value) {
          <li class="dk-badge dk-badge--primary">
            {{ chip.label }}
            <button type="button" class="dk-badge__remove" [attr.aria-label]="'Remove filter ' + chip.label"
              (click)="removeAssignedStatus(chip.value)"></button>
          </li>
          }
          <li>
            <button type="button" class="dk-btn dk-btn--ghost dk-btn--secondary dk-btn--sm" (click)="openFilterMenu()">
              <lucide-icon name="plus" [size]="14" aria-hidden="true" />
              New filter
            </button>
          </li>
        </ul>
        }
      </div>

      <!-- Tabs -->
      <div class="tab-bar" role="tablist">
        @for (tab of tabDefs; track tab.id) {
        <button class="tab-pill" type="button" role="tab" [class.is-active]="activeTab() === tab.id"
          [attr.aria-selected]="activeTab() === tab.id" (click)="selectTab(tab.id)">
          {{ tab.label }}
        </button>
        }
      </div>

      <!-- Results — keyed on the active tab so the whole block remounts (and its fade-in
           keyframe retriggers) on every tab switch, without needing the Animations module -->
      <div class="tab-content">
        @for (tab of [activeTab()]; track tab) {

        @if (showLoading()) {
        <div class="search-state">
          <lucide-icon name="loader-2" [size]="20" class="spin-icon" style="display: flex;" />
          <span>Loading...</span>
        </div>

        } @else if (!hasResults()) {
        @let emptyState = tabEmptyState();
        @if (emptyState) {
        <!-- The header stays to confirm the tab's dataset is empty -->
        <div class="empty-section">
          <div class="section-label">
            <span>{{ emptyState.section }}</span>
            <span class="section-count">0</span>
          </div>
        </div>
        }
        <div class="search-state">
          @if (searchQuery()) {
          <lucide-icon name="search-x" [size]="28" style="display: flex;" />
          <p class="state-title">No results for <strong>"{{ searchQuery() }}"</strong></p>
          <p class="state-sub">Try a different search term</p>
          } @else if (emptyState) {
          <div class="dk-empty-state dk-empty-state--compact tab-empty-state">
            <lucide-icon [name]="emptyState.icon" [size]="35" class="tab-empty-state__icon" aria-hidden="true" />
            <p class="dk-empty-state__title">{{ emptyState.title }}</p>
            <p class="dk-empty-state__description">{{ emptyState.description }}</p>
          </div>
          } @else {
          <p class="state-title">Nothing here yet</p>
          }
        </div>

        } @else {
        <div class="search-results" role="listbox" aria-label="Search results">
          @for (item of filteredFlatItems(); track item.id; let i = $index) {

          @switch (item.type) {

          <!-- Section header (not interactive) -->
          @case ('section') {
          <div class="section-label">
            <span>{{ item.label }}</span>
            @if (item.count !== undefined) {
            <span class="section-count">{{ item.count }}</span>
            }
          </div>
          }

          <!-- Org-level navigation shortcut -->
          @case ('nav') {
          <button #searchItem class="search-item search-item--nav" [class.is-active]="activeIndex() === i"
            [class.is-disabled]="item.disabled" [disabled]="item.disabled" (click)="selectItem(item)"
            (mouseenter)="!item.disabled && activeIndex.set(i)" (mouseleave)="activeIndex.set(-1)" role="option"
            [attr.aria-selected]="activeIndex() === i" type="button">
            <span class="item-icon">
              <lucide-icon [name]="item.icon!" [size]="16" style="display: flex;" />
            </span>
            <span class="item-label">{{ item.label }}</span>
            @if (item.disabledTag) {
            <span class="disabled-tag">{{ item.disabledTag }}</span>
            } @else {
            <lucide-icon name="corner-down-left" [size]="13" class="item-enter" style="display: flex;" />
            }
          </button>
          }

          <!-- Project result -->
          @case ('project') {
          <button #searchItem class="search-item search-item--project" [class.is-active]="activeIndex() === i"
            (click)="selectItem(item)" (mouseenter)="activeIndex.set(i)" (mouseleave)="activeIndex.set(-1)" role="option"
            [attr.aria-selected]="activeIndex() === i" type="button">
            <span class="project-dot" [style.background]="item.color || '#5F42D9'"></span>
            <span class="project-details">
              <span class="project-name">{{ item.label }}</span>
              @if (item.description) {
              <span class="project-desc">{{ item.description }}</span>
              }
            </span>
            <lucide-icon name="corner-down-left" [size]="13" class="item-enter" style="display: flex;" />
          </button>
          }

          <!-- Project sub-action (Sites / Requests / Members) -->
          @case ('sub-action') {
          <button #searchItem class="search-item search-item--sub" [class.is-active]="activeIndex() === i"
            [style.--accent]="item.color || '#5F42D9'" (click)="selectItem(item)" (mouseenter)="activeIndex.set(i)"
            (mouseleave)="activeIndex.set(-1)" role="option" [attr.aria-selected]="activeIndex() === i" type="button">
            <span class="sub-rail"></span>
            <span class="sub-icon">
              <lucide-icon [name]="item.icon!" [size]="14" style="display: flex;" />
            </span>
            <span class="sub-label">{{ item.label }}</span>
            <lucide-icon name="corner-down-left" [size]="13" class="item-enter" style="display: flex;" />
          </button>
          }

          <!-- Recently visited item -->
          @case ('recent') {
          <button #searchItem class="search-item search-item--project" [class.is-active]="activeIndex() === i"
            (click)="selectItem(item)" (mouseenter)="activeIndex.set(i)" (mouseleave)="activeIndex.set(-1)" role="option"
            [attr.aria-selected]="activeIndex() === i" type="button">
            @if (item.icon) {
            <span class="item-icon">
              <lucide-icon [name]="item.icon" [size]="16" style="display: flex;" />
            </span>
            } @else {
            <span class="project-dot" [style.background]="item.color || '#5F42D9'"></span>
            }
            <span class="project-details">
              <span class="project-name">{{ item.label }}</span>
              @if (item.description) {
              <span class="project-desc">{{ item.description }}</span>
              }
            </span>
            <lucide-icon name="corner-down-left" [size]="13" class="item-enter" style="display: flex;" />
          </button>
          }

          <!-- Request assigned to the current user -->
          @case ('active-request') {
          <button #searchItem class="search-item search-item--request" [class.is-active]="activeIndex() === i"
            (click)="selectItem(item)" (mouseenter)="activeIndex.set(i)" (mouseleave)="activeIndex.set(-1)" role="option"
            [attr.aria-selected]="activeIndex() === i" type="button">
            <lucide-icon name="ticket" [size]="18" class="request-icon" aria-hidden="true" />
            <span class="request-details">
              <span class="request-name">{{ item.label }}</span>
              <span class="request-subtitle">— {{ item.subtitle }}</span>
            </span>
            @if (item.priority) {
            <span class="dk-badge priority-badge" [class]="'dk-badge--' + (item.priorityTone ?? 'warning')">
              <lucide-icon name="flag" [size]="11" aria-hidden="true" />
              {{ item.priority }}
            </span>
            }
          </button>
          }

          <!-- Request pending the current user's approval -->
          @case ('pending-approval') {
          <button #searchItem class="search-item search-item--approval" [class.is-active]="activeIndex() === i"
            (click)="selectItem(item)" (mouseenter)="activeIndex.set(i)" (mouseleave)="activeIndex.set(-1)" role="option"
            [attr.aria-selected]="activeIndex() === i" type="button">
            <span class="dk-avatar dk-avatar--sm" aria-hidden="true">
              @if (item.fromHubspot) {
              <app-hubspot-mark label="" />
              } @else if (item.avatarUrl) {
              <img [src]="item.avatarUrl" alt="" />
              } @else {
              {{ item.initials }}
              }
            </span>
            <span class="approval-details">
              <span class="request-name">{{ item.label }}</span>
              <span class="approval-meta">
                <span class="request-subtitle">— <strong>{{ item.requester }}</strong> / {{ item.subtitle }}</span>
                <span class="approval-time">{{ item.timeAgo }}</span>
              </span>
            </span>
          </button>
          }

          }
          }
        </div>
        }

        }
      </div>

      <!-- Keyboard hints -->
      <div class="search-footer">
        <span class="hint"><kbd>↑</kbd><kbd>↓</kbd> Navigate</span>
        <span class="hint"><kbd>↵</kbd> Open</span>
        <span class="hint"><kbd>Esc</kbd> Close</span>
      </div>

    </div>
  </div>
  }

</div>
